import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { CalendarIcon, Clock, LogIn, LogOut, CheckCircle, XCircle, FileSpreadsheet, CalendarDays, CalendarRange, AlertCircle, Palmtree, Plus, Trash2, CalendarHeart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { format, differenceInDays } from 'date-fns';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026 Bank Holidays for India
const DEFAULT_HOLIDAYS_2026 = [
  { name: "Republic Day", date: "2026-01-26" },
  { name: "Maha Shivaratri", date: "2026-02-15" },
  { name: "Holi", date: "2026-03-10" },
  { name: "Good Friday", date: "2026-04-03" },
  { name: "Ram Navami", date: "2026-04-04" },
  { name: "Mahavir Jayanti", date: "2026-04-06" },
  { name: "Dr. Ambedkar Jayanti", date: "2026-04-14" },
  { name: "Buddha Purnima", date: "2026-05-14" },
  { name: "Eid ul-Fitr", date: "2026-05-17" },
  { name: "Eid ul-Adha", date: "2026-07-24" },
  { name: "Independence Day", date: "2026-08-15" },
  { name: "Janmashtami", date: "2026-08-25" },
  { name: "Muharram", date: "2026-08-25" },
  { name: "Milad-un-Nabi", date: "2026-10-24" },
  { name: "Gandhi Jayanti", date: "2026-10-02" },
  { name: "Dussehra", date: "2026-10-13" },
  { name: "Diwali", date: "2026-11-02" },
  { name: "Diwali (Lakshmi Puja)", date: "2026-11-03" },
  { name: "Guru Nanak Jayanti", date: "2026-11-19" },
  { name: "Christmas", date: "2026-12-25" }
];

const AttendancePage = () => {
  const { user, isAdmin } = useAuth();
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [activeTab, setActiveTab] = useState('attendance');
  const [viewMode, setViewMode] = useState('day'); // day, week, month
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Holiday management states
  const [isHolidayDialogOpen, setIsHolidayDialogOpen] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '' });

  // Admin apply-leave-for-employee dialog
  const [isAdminLeaveDialogOpen, setIsAdminLeaveDialogOpen] = useState(false);
  const [adminLeaveForm, setAdminLeaveForm] = useState({
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    status: 'approved',
  });

  const resetAdminLeaveForm = () => setAdminLeaveForm({
    employee_id: '', leave_type: 'casual', start_date: '', end_date: '', reason: '', status: 'approved'
  });

  const handleAdminApplyLeave = async () => {
    if (!adminLeaveForm.employee_id || !adminLeaveForm.start_date || !adminLeaveForm.reason) {
      toast.error('Employee, start date आणि reason आवश्यक आहेत');
      return;
    }
    const emp = employees.find(e => e.id === adminLeaveForm.employee_id);
    if (!emp) { toast.error('Invalid employee'); return; }
    try {
      // 1) Create leave application
      const createRes = await axios.post(`${API}/leaves`, {
        employee_id: adminLeaveForm.employee_id,
        employee_name: emp.full_name || emp.name || emp.username,
        leave_type: adminLeaveForm.leave_type,
        start_date: adminLeaveForm.start_date,
        end_date: adminLeaveForm.end_date || adminLeaveForm.start_date,
        reason: adminLeaveForm.reason,
      });
      const leaveId = createRes.data?.id;
      // 2) If admin chose status approved/rejected, immediately update
      if (leaveId && adminLeaveForm.status !== 'pending') {
        await axios.put(`${API}/leaves/${leaveId}`, {
          status: adminLeaveForm.status,
          admin_remarks: 'Applied by admin on behalf of employee'
        });
      }
      toast.success(`Leave applied for ${emp.full_name || emp.username} (${adminLeaveForm.status})`);
      setIsAdminLeaveDialogOpen(false);
      resetAdminLeaveForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Leave apply करण्यात अडचण');
    }
  };

  useEffect(() => {
    fetchData();
    fetchHolidays();
  }, [selectedDate, selectedEmployee, viewMode]);

  const fetchHolidays = async () => {
    try {
      const response = await axios.get(`${API}/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays');
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Please fill holiday name and date');
      return;
    }
    try {
      const year = new Date(newHoliday.date).getFullYear();
      await axios.post(`${API}/holidays`, { ...newHoliday, year });
      toast.success('Holiday added successfully');
      setNewHoliday({ name: '', date: '' });
      setIsHolidayDialogOpen(false);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding holiday');
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await axios.delete(`${API}/holidays/${holidayId}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Error deleting holiday');
    }
  };

  const handleAddDefaultHolidays = async () => {
    try {
      let addedCount = 0;
      for (const holiday of DEFAULT_HOLIDAYS_2026) {
        try {
          const year = new Date(holiday.date).getFullYear();
          await axios.post(`${API}/holidays`, { ...holiday, year });
          addedCount++;
        } catch (e) {
          // Skip if already exists
        }
      }
      toast.success(`Added ${addedCount} holidays for 2026`);
      fetchHolidays();
    } catch (error) {
      toast.error('Error adding default holidays');
    }
  };

  const getDateRange = () => {
    const now = selectedDate;
    let start, end;
    
    if (viewMode === 'day') {
      start = format(now, 'yyyy-MM-dd');
      end = start;
    } else if (viewMode === 'week') {
      // Get start of week (Monday)
      const dayOfWeek = now.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() + diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      start = format(startOfWeek, 'yyyy-MM-dd');
      end = format(endOfWeek, 'yyyy-MM-dd');
    } else if (viewMode === 'month') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      start = format(startOfMonth, 'yyyy-MM-dd');
      end = format(endOfMonth, 'yyyy-MM-dd');
    }
    
    return { start, end };
  };

  const fetchData = async () => {
    try {
      const { start, end } = getDateRange();
      setDateRange({ start, end });
      
      const params = { start_date: start, end_date: end };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }

      const [attendanceRes, employeesRes, leavesRes] = await Promise.all([
        axios.get(`${API}/attendance`, { params }),
        axios.get(`${API}/employees`),
        axios.get(`${API}/leaves`)
      ]);

      setAttendance(attendanceRes.data);
      setEmployees(employeesRes.data);
      setLeaves(leavesRes.data);

      // Check today's attendance for current user
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayRes = await axios.get(`${API}/attendance`, {
        params: { employee_id: user?.id, start_date: today, end_date: today }
      });
      setTodayAttendance(todayRes.data?.[0] || null);
    } catch (error) {
      toast.error('Error fetching attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      const response = await axios.post(`${API}/attendance/check-in`);
      toast.success(`Checked in at ${response.data.time}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error checking in');
    }
  };

  const handleCheckOut = async () => {
    try {
      const response = await axios.post(`${API}/attendance/check-out`);
      toast.success(`Checked out at ${response.data.time}. Total: ${response.data.total_hours} hours`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error checking out');
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time;
  };

  // Export attendance report
  const exportAttendanceReport = async (period) => {
    try {
      const now = new Date();
      let startDate, endDate;
      endDate = new Date(now);
      
      switch (period) {
        case 'weekly':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case 'monthly':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'yearly':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
      }

      // Fetch all attendance records
      const response = await axios.get(`${API}/attendance`);
      const allAttendance = response.data.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate >= startDate && recordDate <= endDate;
      });

      if (allAttendance.length === 0) {
        toast.warning('No attendance records found for this period');
        return;
      }

      // Group by employee
      const employeeData = {};
      allAttendance.forEach(record => {
        if (!employeeData[record.employee_name]) {
          employeeData[record.employee_name] = {
            records: [],
            totalDays: 0,
            totalHours: 0
          };
        }
        employeeData[record.employee_name].records.push(record);
        if (record.status === 'present') employeeData[record.employee_name].totalDays++;
        employeeData[record.employee_name].totalHours += record.total_hours || 0;
      });

      // Create detailed report
      const detailedData = allAttendance.map(r => ({
        'Employee Name': r.employee_name,
        'Date': format(new Date(r.date), 'dd MMM yyyy'),
        'Day': format(new Date(r.date), 'EEEE'),
        'Check In': r.check_in || '-',
        'Check Out': r.check_out || '-',
        'Total Hours': r.total_hours ? `${r.total_hours} hrs` : '-',
        'Status': r.status?.charAt(0).toUpperCase() + r.status?.slice(1)
      }));

      // Create summary
      const summaryData = Object.entries(employeeData).map(([name, data]) => ({
        'Employee Name': name,
        'Present Days': data.totalDays,
        'Total Hours': data.totalHours.toFixed(1),
        'Avg Hours/Day': data.totalDays > 0 ? (data.totalHours / data.totalDays).toFixed(1) : '0'
      }));

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'Summary');
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(detailedData), 'Detailed Records');

      const fileName = `Attendance_Report_${period}_${format(new Date(), 'yyyy-MM-dd')}`;
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
      toast.success(`${period} attendance report downloaded!`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error exporting attendance report');
    }
  };

  // Leave approval functions
  const handleApproveLeave = async (leaveId) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}`, { status: 'approved' });
      toast.success('Leave approved successfully');
      fetchData();
    } catch (error) {
      toast.error('Error approving leave');
    }
  };

  const handleRejectLeave = async (leaveId) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}`, { status: 'rejected' });
      toast.success('Leave rejected');
      fetchData();
    } catch (error) {
      toast.error('Error rejecting leave');
    }
  };

  const getLeaveTypeBadge = (type) => {
    const types = {
      casual: { label: 'Casual', color: 'bg-blue-100 text-blue-700' },
      sick: { label: 'Sick', color: 'bg-red-100 text-red-700' },
      earned: { label: 'Earned', color: 'bg-emerald-100 text-emerald-700' },
      unpaid: { label: 'Unpaid', color: 'bg-slate-100 text-slate-700' },
      half_day: { label: 'Half Day', color: 'bg-amber-100 text-amber-700' },
    };
    const info = types[type] || { label: type, color: 'bg-slate-100 text-slate-700' };
    return <Badge className={info.color}>{info.label}</Badge>;
  };

  // Leave stats
  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const approvedLeaves = leaves.filter(l => l.status === 'approved');
  const rejectedLeaves = leaves.filter(l => l.status === 'rejected');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="attendance-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Attendance</h1>
          <p className="text-slate-500 mt-1">Track employee attendance and work hours</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="text-xs text-slate-500">Attendance Report</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => exportAttendanceReport('weekly')} className="cursor-pointer">
              <CalendarIcon className="h-4 w-4 mr-2 text-blue-500" />
              Weekly Report
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAttendanceReport('monthly')} className="cursor-pointer">
              <CalendarDays className="h-4 w-4 mr-2 text-emerald-500" />
              Monthly Report
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAttendanceReport('yearly')} className="cursor-pointer">
              <CalendarRange className="h-4 w-4 mr-2 text-purple-500" />
              Yearly Report
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* View Mode Selector */}
      {isAdmin && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-600">View:</span>
                <div className="flex border rounded-lg overflow-hidden">
                  <Button
                    variant={viewMode === 'day' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('day')}
                    className={viewMode === 'day' ? 'bg-brand-navy text-white' : ''}
                  >
                    Day
                  </Button>
                  <Button
                    variant={viewMode === 'week' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('week')}
                    className={viewMode === 'week' ? 'bg-brand-navy text-white' : ''}
                  >
                    Week
                  </Button>
                  <Button
                    variant={viewMode === 'month' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('month')}
                    className={viewMode === 'month' ? 'bg-brand-navy text-white' : ''}
                  >
                    Month
                  </Button>
                </div>
              </div>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(selectedDate, 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {viewMode !== 'day' && dateRange.start && (
                <Badge variant="outline" className="text-sm py-1 px-3 bg-brand-navy/5 text-brand-navy">
                  {format(new Date(dateRange.start), 'dd MMM')} - {format(new Date(dateRange.end), 'dd MMM yyyy')}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Check In/Out Card */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-brand-navy to-brand-navy-light text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">Today's Attendance</h3>
              <p className="text-slate-300 text-sm">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
              {todayAttendance && (
                <div className="flex items-center gap-4 mt-3">
                  {todayAttendance.check_in && (
                    <div className="flex items-center gap-2">
                      <LogIn className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm">In: {todayAttendance.check_in}</span>
                    </div>
                  )}
                  {todayAttendance.check_out && (
                    <div className="flex items-center gap-2">
                      <LogOut className="h-4 w-4 text-amber-400" />
                      <span className="text-sm">Out: {todayAttendance.check_out}</span>
                    </div>
                  )}
                  {todayAttendance.total_hours > 0 && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-400" />
                      <span className="text-sm">{todayAttendance.total_hours} hours</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCheckIn}
                data-testid="check-in-btn"
                disabled={todayAttendance?.check_in}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Check In
              </Button>
              <Button
                onClick={handleCheckOut}
                data-testid="check-out-btn"
                disabled={!todayAttendance?.check_in || todayAttendance?.check_out}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Check Out
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-brand-navy">
            Attendance Records {viewMode === 'day' ? `- ${format(selectedDate, 'MMMM d, yyyy')}` : 
              viewMode === 'week' ? '- This Week' : '- This Month'}
            {dateRange.start && viewMode !== 'day' && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({format(new Date(dateRange.start), 'dd MMM')} - {format(new Date(dateRange.end), 'dd MMM yyyy')})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Employee</TableHead>
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Check In</TableHead>
                <TableHead className="font-semibold text-slate-600">Check Out</TableHead>
                <TableHead className="font-semibold text-slate-600">Total Hours</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendance.map((record) => (
                <TableRow key={record.id} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium">{record.employee_name}</TableCell>
                  <TableCell>{format(new Date(record.date), 'dd MMM yyyy')}</TableCell>
                  <TableCell>
                    {record.check_in ? (
                      <div className="flex items-center gap-2 text-emerald-600">
                        <LogIn className="h-4 w-4" />
                        {formatTime(record.check_in)}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {record.check_out ? (
                      <div className="flex items-center gap-2 text-amber-600">
                        <LogOut className="h-4 w-4" />
                        {formatTime(record.check_out)}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {record.total_hours > 0 ? (
                      <span className="font-semibold">{record.total_hours} hrs</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      record.status === 'present'
                        ? 'bg-emerald-100 text-emerald-700'
                        : record.status === 'half-day'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                    }>
                      {record.status === 'present' ? (
                        <><CheckCircle className="h-3 w-3 mr-1" /> Present</>
                      ) : record.status === 'half-day' ? (
                        <><Clock className="h-3 w-3 mr-1" /> Half Day</>
                      ) : (
                        <><XCircle className="h-3 w-3 mr-1" /> Absent</>
                      )}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {attendance.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    No attendance records found for this date
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Leave Approval Section - Admin Only */}
      {isAdmin && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
                <Palmtree className="h-5 w-5" />
                Leave Applications
                {pendingLeaves.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 ml-2">
                    {pendingLeaves.length} Pending
                  </Badge>
                )}
              </CardTitle>
              <div className="flex gap-2 items-center">
                <Badge className="bg-emerald-100 text-emerald-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {approvedLeaves.length} Approved
                </Badge>
                <Badge className="bg-red-100 text-red-700">
                  <XCircle className="h-3 w-3 mr-1" />
                  {rejectedLeaves.length} Rejected
                </Badge>
                <Button
                  size="sm"
                  onClick={() => { resetAdminLeaveForm(); setIsAdminLeaveDialogOpen(true); }}
                  className="bg-brand-navy text-white hover:bg-brand-navy/90 ml-2"
                  data-testid="admin-apply-leave-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Apply Leave
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs defaultValue="pending" className="w-full">
              <div className="px-6 pt-2">
                <TabsList className="grid w-full max-w-md grid-cols-3">
                  <TabsTrigger value="pending" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Pending ({pendingLeaves.length})
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Approved
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    Rejected
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="pending" className="mt-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingLeaves.map((leave) => {
                      const days = differenceInDays(new Date(leave.end_date), new Date(leave.start_date)) + 1;
                      return (
                        <TableRow key={leave.id} className="hover:bg-amber-50/50">
                          <TableCell className="font-medium">{leave.employee_name}</TableCell>
                          <TableCell>{getLeaveTypeBadge(leave.leave_type)}</TableCell>
                          <TableCell>{format(new Date(leave.start_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>{format(new Date(leave.end_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>{days} day{days > 1 ? 's' : ''}</TableCell>
                          <TableCell className="max-w-[200px] truncate" title={leave.reason}>
                            {leave.reason}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveLeave(leave.id)}
                                data-testid={`approve-leave-${leave.id}`}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectLeave(leave.id)}
                                data-testid={`reject-leave-${leave.id}`}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {pendingLeaves.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                          <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                          No pending leave applications
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="approved" className="mt-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvedLeaves.map((leave) => {
                      const days = differenceInDays(new Date(leave.end_date), new Date(leave.start_date)) + 1;
                      return (
                        <TableRow key={leave.id} className="hover:bg-emerald-50/50">
                          <TableCell className="font-medium">{leave.employee_name}</TableCell>
                          <TableCell>{getLeaveTypeBadge(leave.leave_type)}</TableCell>
                          <TableCell>{format(new Date(leave.start_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>{format(new Date(leave.end_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>{days} day{days > 1 ? 's' : ''}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{leave.reason}</TableCell>
                        </TableRow>
                      );
                    })}
                    {approvedLeaves.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                          No approved leaves
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="rejected" className="mt-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rejectedLeaves.map((leave) => {
                      const days = differenceInDays(new Date(leave.end_date), new Date(leave.start_date)) + 1;
                      return (
                        <TableRow key={leave.id} className="hover:bg-red-50/50">
                          <TableCell className="font-medium">{leave.employee_name}</TableCell>
                          <TableCell>{getLeaveTypeBadge(leave.leave_type)}</TableCell>
                          <TableCell>{format(new Date(leave.start_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>{format(new Date(leave.end_date), 'dd MMM yyyy')}</TableCell>
                          <TableCell>{days} day{days > 1 ? 's' : ''}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{leave.reason}</TableCell>
                        </TableRow>
                      );
                    })}
                    {rejectedLeaves.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                          No rejected leaves
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Bank Holidays Management - Admin Only */}
      {isAdmin && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-brand-navy flex items-center gap-2">
                <CalendarHeart className="h-5 w-5" />
                Bank Holidays 2026
                <Badge className="bg-purple-100 text-purple-700 ml-2">
                  {holidays.length} Holidays
                </Badge>
              </CardTitle>
              <div className="flex gap-2">
                {holidays.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddDefaultHolidays}
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add 2026 Holidays
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => setIsHolidayDialogOpen(true)}
                  data-testid="add-holiday-btn"
                  className="bg-brand-navy text-white hover:bg-brand-navy/90"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Holiday
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-600">Holiday Name</TableHead>
                  <TableHead className="font-semibold text-slate-600">Date</TableHead>
                  <TableHead className="font-semibold text-slate-600">Day</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((holiday) => (
                  <TableRow key={holiday.id} className="hover:bg-purple-50/50">
                    <TableCell className="font-medium">{holiday.name}</TableCell>
                    <TableCell>{format(new Date(holiday.date), 'dd MMM yyyy')}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {format(new Date(holiday.date), 'EEEE')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                        data-testid={`delete-holiday-${holiday.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {holidays.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-slate-400">
                      <CalendarHeart className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      No holidays added yet. Click "Add 2026 Holidays" to add default bank holidays.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Holiday Dialog */}
      <Dialog open={isHolidayDialogOpen} onOpenChange={setIsHolidayDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Add Bank Holiday</DialogTitle>
            <DialogDescription>Add a new bank holiday to the calendar</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                data-testid="input-holiday-name"
                placeholder="e.g., Diwali, Republic Day"
              />
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                data-testid="input-holiday-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsHolidayDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddHoliday}
              data-testid="submit-holiday-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!newHoliday.name || !newHoliday.date}
            >
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: Apply Leave for Employee Dialog */}
      <Dialog open={isAdminLeaveDialogOpen} onOpenChange={setIsAdminLeaveDialogOpen}>
        <DialogContent className="max-w-md" data-testid="admin-leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Apply Leave (Admin)</DialogTitle>
            <DialogDescription>Employee च्या वतीने leave apply करा. Backdated leaves देखील allowed आहेत.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={adminLeaveForm.employee_id}
                onValueChange={(v) => setAdminLeaveForm(prev => ({ ...prev, employee_id: v }))}
              >
                <SelectTrigger data-testid="admin-leave-employee">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.full_name || emp.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leave Type *</Label>
              <Select
                value={adminLeaveForm.leave_type}
                onValueChange={(v) => setAdminLeaveForm(prev => ({ ...prev, leave_type: v }))}
              >
                <SelectTrigger data-testid="admin-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="earned">Earned Leave (Paid)</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>From Date *</Label>
                <Input
                  type="date"
                  value={adminLeaveForm.start_date}
                  onChange={(e) => setAdminLeaveForm(prev => ({ ...prev, start_date: e.target.value }))}
                  data-testid="admin-leave-start"
                />
              </div>
              <div className="space-y-2">
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={adminLeaveForm.end_date}
                  onChange={(e) => setAdminLeaveForm(prev => ({ ...prev, end_date: e.target.value }))}
                  data-testid="admin-leave-end"
                  min={adminLeaveForm.start_date || undefined}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input
                value={adminLeaveForm.reason}
                onChange={(e) => setAdminLeaveForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Medical, family emergency"
                data-testid="admin-leave-reason"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={adminLeaveForm.status}
                onValueChange={(v) => setAdminLeaveForm(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger data-testid="admin-leave-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAdminLeaveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAdminApplyLeave}
              data-testid="admin-leave-submit"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
            >
              Apply Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
